import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { providerAccessAPI } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { 
  Users, 
  UserPlus, 
  Loader2, 
  Copy, 
  CheckCircle, 
  XCircle,
  Mail,
  Phone,
  Building2,
  Shield,
  Clock,
  AlertTriangle
} from 'lucide-react'

interface StaffMember {
  id: string
  staff_code: string
  first_name: string
  last_name: string
  email: string
  role: string
  department?: string
  phone?: string
  is_active: boolean
  is_admin: boolean
  last_login_at?: string
  created_at: string
}

interface NewStaffData {
  firstName: string
  lastName: string
  email: string
  role: string
  department: string
  phone: string
  isAdmin: boolean
}

export default function StaffManagementPage() {
  const { toast } = useToast()
  const [staff, setStaff] = useState<StaffMember[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAddingStaff, setIsAddingStaff] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false)
  const [staffToDeactivate, setStaffToDeactivate] = useState<StaffMember | null>(null)
  const [isDeactivating, setIsDeactivating] = useState(false)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)
  const [newStaff, setNewStaff] = useState<NewStaffData>({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    department: '',
    phone: '',
    isAdmin: false
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    fetchStaff()
  }, [])

  const fetchStaff = async () => {
    try {
      setIsLoading(true)
      const response = await providerAccessAPI.getStaffMembers()
      if (response.success) {
        setStaff(response.data.staff || [])
      }
    } catch (error) {
      console.error('Failed to fetch staff:', error)
      toast({
        title: "Error",
        description: "Could not load staff members. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsLoading(false)
    }
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!newStaff.firstName.trim()) {
      errors.firstName = 'First name is required'
    }
    if (!newStaff.lastName.trim()) {
      errors.lastName = 'Last name is required'
    }
    if (!newStaff.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newStaff.email)) {
      errors.email = 'Invalid email format'
    }
    if (!newStaff.role.trim()) {
      errors.role = 'Role is required'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleAddStaff = async () => {
    if (!validateForm()) {
      return
    }

    setIsAddingStaff(true)
    try {
      const response = await providerAccessAPI.addStaffMember(newStaff)
      if (response.success) {
        toast({
          title: "Staff Member Added",
          description: `${newStaff.firstName} ${newStaff.lastName} has been added with code ${response.data.staff.staff_code}`,
        })
        setIsDialogOpen(false)
        setNewStaff({
          firstName: '',
          lastName: '',
          email: '',
          role: '',
          department: '',
          phone: '',
          isAdmin: false
        })
        fetchStaff()
      } else {
        toast({
          title: "Error",
          description: response.message || "Could not add staff member",
          variant: "destructive"
        })
      }
    } catch (error: any) {
      console.error('Failed to add staff:', error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Could not add staff member. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsAddingStaff(false)
    }
  }

  const handleDeactivateStaff = async () => {
    if (!staffToDeactivate) return

    setIsDeactivating(true)
    try {
      const response = await providerAccessAPI.deactivateStaffMember(staffToDeactivate.id)
      if (response.success) {
        toast({
          title: "Staff Deactivated",
          description: `${staffToDeactivate.first_name} ${staffToDeactivate.last_name} has been deactivated`,
        })
        setDeactivateDialogOpen(false)
        setStaffToDeactivate(null)
        fetchStaff()
      } else {
        toast({
          title: "Error",
          description: response.message || "Could not deactivate staff member",
          variant: "destructive"
        })
      }
    } catch (error) {
      console.error('Failed to deactivate staff:', error)
      toast({
        title: "Error",
        description: "Could not deactivate staff member. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsDeactivating(false)
    }
  }

  const copyStaffCode = async (code: string) => {
    await navigator.clipboard.writeText(code)
    setCopiedCode(code)
    toast({
      title: "Copied!",
      description: "Staff code copied to clipboard",
    })
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const handleInputChange = (field: keyof NewStaffData, value: string | boolean) => {
    setNewStaff(prev => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const activeStaff = staff.filter(s => s.is_active)
  const inactiveStaff = staff.filter(s => !s.is_active)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#D64463]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Staff Management</h1>
          <p className="text-gray-600">Manage staff members who can access patient records</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-[#D64463] to-[#9C27B0] hover:opacity-90">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Staff Member
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add New Staff Member</DialogTitle>
              <DialogDescription>
                Add a new staff member to your organization. They will receive a unique staff code for mobile app login.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={newStaff.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={formErrors.firstName ? 'border-red-500' : ''}
                  />
                  {formErrors.firstName && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.firstName}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={newStaff.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={formErrors.lastName ? 'border-red-500' : ''}
                  />
                  {formErrors.lastName && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={formErrors.email ? 'border-red-500' : ''}
                />
                {formErrors.email && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="role">Role *</Label>
                  <Input
                    id="role"
                    value={newStaff.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    placeholder="e.g., Physician, Nurse"
                    className={formErrors.role ? 'border-red-500' : ''}
                  />
                  {formErrors.role && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.role}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    value={newStaff.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    placeholder="e.g., Cardiology"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={newStaff.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isAdmin"
                  checked={newStaff.isAdmin}
                  onChange={(e) => handleInputChange('isAdmin', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-[#D64463] focus:ring-[#D64463]"
                />
                <Label htmlFor="isAdmin" className="text-sm font-normal">
                  Grant admin privileges (can manage other staff)
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddStaff} 
                disabled={isAddingStaff}
                className="bg-gradient-to-r from-[#D64463] to-[#9C27B0]"
              >
                {isAddingStaff ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Staff Member'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <Users className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{activeStaff.length}</p>
                <p className="text-sm text-gray-500">Active Staff</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-xl">
                <XCircle className="h-6 w-6 text-gray-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{inactiveStaff.length}</p>
                <p className="text-sm text-gray-500">Inactive Staff</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {staff.filter(s => s.is_admin && s.is_active).length}
                </p>
                <p className="text-sm text-gray-500">Administrators</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Staff Members</CardTitle>
          <CardDescription>
            All staff members in your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {staff.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No staff members yet</h3>
              <p className="text-gray-500 mb-4">Add your first staff member to get started</p>
              <Button 
                onClick={() => setIsDialogOpen(true)}
                className="bg-gradient-to-r from-[#D64463] to-[#9C27B0]"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Staff Member
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {staff.map((member) => (
                <div 
                  key={member.id} 
                  className={`p-4 rounded-xl border ${member.is_active ? 'bg-white border-gray-100' : 'bg-gray-50 border-gray-200'}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold ${
                        member.is_active 
                          ? 'bg-gradient-to-br from-[#D64463] to-[#9C27B0]' 
                          : 'bg-gray-400'
                      }`}>
                        {member.first_name.charAt(0)}{member.last_name.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">
                            {member.first_name} {member.last_name}
                          </h3>
                          {member.is_admin && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">Admin</Badge>
                          )}
                          {!member.is_active && (
                            <Badge variant="secondary" className="text-xs">Inactive</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{member.role}</p>
                        {member.department && (
                          <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                            <Building2 className="h-3 w-3" />
                            {member.department}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      {/* Staff Code */}
                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <p className="text-xs text-gray-500 mb-1">Staff Code</p>
                        <div className="flex items-center gap-2">
                          <code className="font-mono font-semibold text-[#D64463]">
                            {member.staff_code}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => copyStaffCode(member.staff_code)}
                          >
                            {copiedCode === member.staff_code ? (
                              <CheckCircle className="h-3 w-3 text-emerald-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Contact Info */}
                      <div className="text-sm text-gray-500 space-y-1">
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <span className="truncate max-w-[200px]">{member.email}</span>
                        </div>
                        {member.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            <span>{member.phone}</span>
                          </div>
                        )}
                      </div>

                      {/* Last Login */}
                      <div className="text-xs text-gray-400">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {member.last_login_at ? (
                            <span>Last login: {new Date(member.last_login_at).toLocaleDateString()}</span>
                          ) : (
                            <span>Never logged in</span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      {member.is_active && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setStaffToDeactivate(member)
                            setDeactivateDialogOpen(true)
                          }}
                        >
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Deactivate Staff Member
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate {staffToDeactivate?.first_name} {staffToDeactivate?.last_name}? 
              This will immediately revoke all their active patient access sessions.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
            <p className="text-sm text-amber-800">
              <strong>Warning:</strong> This action will:
            </p>
            <ul className="text-sm text-amber-700 mt-2 list-disc list-inside space-y-1">
              <li>Prevent the staff member from logging in</li>
              <li>Immediately end all active patient access sessions</li>
              <li>Log this action in the audit trail</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeactivateStaff}
              disabled={isDeactivating}
            >
              {isDeactivating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deactivating...
                </>
              ) : (
                'Deactivate Staff Member'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
